import { useState, useMemo } from "react";
import { useApp } from "../contexts/AppContext";
import PostCard from "../components/PostCard";

const PAGE_SIZE = 6;

const fmtDate = iso =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-AU",
    { day:"numeric", month:"short", year:"numeric" }) : "";

export default function NewsPage() {
  const { posts } = useApp();

  const [search,   setSearch]   = useState("");
  const [cat,      setCat]      = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [sortAsc,  setSortAsc]  = useState(false); /* false = newest first */
  const [page,     setPage]     = useState(1);

  const cats = ["All", ...Array.from(new Set(posts.map(p => p.category).filter(Boolean)))];

  const hasFilters = search || cat !== "All" || dateFrom || dateTo;

  /* ── Filtered + sorted list ──────────────────────────────────────── */
  const filtered = useMemo(() => {
    return [...posts]
      .filter(p => {
        if (!p.published) return false;
        if (cat !== "All" && p.category !== cat) return false;
        if (search) {
          const q = search.toLowerCase();
          if (!p.title.toLowerCase().includes(q) &&
              !p.excerpt.toLowerCase().includes(q)) return false;
        }
        if (dateFrom && p.date < dateFrom) return false;
        if (dateTo   && p.date > dateTo)   return false;
        return true;
      })
      .sort((a, b) =>
        sortAsc
          ? new Date(a.date) - new Date(b.date)
          : new Date(b.date) - new Date(a.date)
      );
  }, [posts, search, cat, dateFrom, dateTo, sortAsc]);

  /* ── Pagination ──────────────────────────────────────────────────── */
  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated   = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  /* ── Helpers that reset page on filter change ────────────────────── */
  const go = fn => { fn(); setPage(1); };

  const resetAll = () => {
    setSearch(""); setCat("All");
    setDateFrom(""); setDateTo("");
    setSortAsc(false); setPage(1);
  };

  /* ── Results label ───────────────────────────────────────────────── */
  const resultsLabel = (() => {
    const parts = [];
    if (dateFrom) parts.push(`from ${fmtDate(dateFrom)}`);
    if (dateTo)   parts.push(`to ${fmtDate(dateTo)}`);
    return `${filtered.length} article${filtered.length !== 1 ? "s" : ""}` +
           (parts.length ? " " + parts.join(" ") : "");
  })();

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div>
      <div className="pg-hdr">
        <h1>News &amp; Announcements</h1>
        <p>Latest updates, events, and project developments from LHDA.</p>
      </div>

      <section className="sec">
        <div className="container">

          {/* ── Filter panel ── */}
          <div className="news-filters">

            {/* Row 1 — search + category pills */}
            <div className="news-filter-row">
              <input
                className="news-search"
                placeholder="🔍  Search announcements…"
                value={search}
                onChange={e => go(() => setSearch(e.target.value))}
              />
              <div className="news-cats">
                {cats.map(c => (
                  <button
                    key={c}
                    className={`news-cat-btn${cat === c ? " on" : ""}`}
                    onClick={() => go(() => setCat(c))}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Row 2 — date range + sort + clear */}
            <div className="news-filter-row news-date-row">
              <div className="news-date-group">
                <span className="news-date-lbl">From</span>
                <input
                  type="date"
                  className="news-date-inp"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={e => go(() => setDateFrom(e.target.value))}
                />
                <span className="news-date-sep">—</span>
                <span className="news-date-lbl">To</span>
                <input
                  type="date"
                  className="news-date-inp"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={e => go(() => setDateTo(e.target.value))}
                />
              </div>

              <div style={{ display:"flex", gap:".5rem", alignItems:"center", flexShrink:0 }}>
                <button
                  className="news-sort-btn"
                  onClick={() => go(() => setSortAsc(s => !s))}
                  title="Toggle sort order"
                >
                  {sortAsc ? "Oldest First ↑" : "Newest First ↓"}
                </button>
                {hasFilters && (
                  <button className="news-clear-btn" onClick={resetAll}>✕ Clear</button>
                )}
              </div>
            </div>
          </div>

          {/* ── Results count ── */}
          {(hasFilters || !sortAsc) && filtered.length > 0 && (
            <div className="news-results-info">{resultsLabel}</div>
          )}

          {/* ── Articles grid ── */}
          {filtered.length === 0 ? (
            <div className="empty-state">
              No articles found.{hasFilters && (
                <> Try <button className="news-inline-clear" onClick={resetAll}>clearing your filters</button>.</>
              )}
            </div>
          ) : (
            <>
              <div className="grid3">
                {paginated.map(post => <PostCard key={post.id} post={post}/>)}
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button disabled={currentPage === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button key={n} className={currentPage === n ? "active" : ""} onClick={() => setPage(n)}>
                      {n}
                    </button>
                  ))}
                  <button disabled={currentPage === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                  <span className="page-info">Page {currentPage} of {totalPages}</span>
                </div>
              )}
            </>
          )}

        </div>
      </section>
    </div>
  );
}
